'use client';

import { useState } from 'react';

import { IconButton } from '@/decodo/components/atoms/IconButton';

import { Carousel } from '@/decodo/components/atoms/Carousel';
import NextImage from '@/decodo/components/atoms/Image';
import type { Swiper } from 'swiper/types';
import IconArrowLeft from '~/icons/arrow-left.svg';
import IconArrowRight from '~/icons/arrow-right.svg';
import { carouselConfig } from '../constants/carousel';
import { CarouselCard, CarouselCardProps } from './CarouselCard';

import { cn } from '@/lib/utils';

import styles from './HomepageCarousel.module.css';

export type HomepageCarouselProps = {
  slides: CarouselCardProps['slides'];
};

export function HomepageCarousel({ slides }: HomepageCarouselProps) {
  const [swiper, setSwiper] = useState<Swiper>();
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);

  return (
    <section className='hidden w-full grid-cols-[48px_1fr_48px] grid-rows-1 items-center justify-between gap-x-10 xl:grid'>
      <div className='shrink-0'>
        <IconButton
          onClick={() => swiper?.slidePrev()}
          color='secondary'
          size='l'
          classNames={{
            root: 'dark:bg-gray-100 bg-gray-100 text-base-black rounded-full dark:hover:bg-gray-300  hover:bg-gray-200',
          }}
        >
          <IconArrowLeft className='h-8 w-8' />
        </IconButton>
      </div>
      <div
        className={cn(
          'transform-3d relative z-[1] aspect-[1104/600] max-h-[600px] max-w-full origin-top',
          styles.heroCarousel
        )}
      >
        <div className='transform-3d h-full w-full'>
          <Carousel
            config={{
              onSwiper: (swiper) => setSwiper(swiper),
              onSlideChange: (swiper) => setActiveSlideIndex(swiper.realIndex),
              ...carouselConfig,
            }}
            classNames={{
              root: 'rounded-[4px] h-full w-[92.75%] overflow-hidden',
            }}
            sideGradient={false}
            items={slides}
            renderItem={({ item, index }) => (
              <div className='aspect-[1024/600] max-h-[600px]'>
                <NextImage
                  {...slides[index].image}
                  objectFit='cover'
                  classNames={{
                    container:
                      'h-full full rounded-[4px] overflow-hidden border border-gray-200',
                    image: 'w-full h-full object-left-top',
                  }}
                />
              </div>
            )}
          />
        </div>
        <div className='absolute right-0 top-20 z-[1] max-w-[372px]'>
          <div className='h-full w-full'>
            <CarouselCard
              swiper={swiper}
              slides={slides}
              activeSlideIndex={activeSlideIndex}
            />
          </div>
        </div>
      </div>
      <div className='shrink-0'>
        <IconButton
          onClick={() => swiper?.slideNext()}
          color='secondary'
          size='l'
          classNames={{
            root: 'dark:bg-gray-100 bg-gray-100 text-base-black rounded-full dark:hover:bg-gray-300  hover:bg-gray-200',
          }}
        >
          <IconArrowRight className='h-8 w-8' />
        </IconButton>
      </div>
    </section>
  );
}
